import React, { useEffect, useState } from "react";
import ErrorField from "../../../Components/ErrorField";
import DatePickerComponent from "../../../Components/DatePickerComponent";
import InfoPopUp from "../../../Components/InfoPopUp";
import dayjs from "dayjs";
import { AllowOnlNumeric } from "../../../Helpers/Utilities";
import {
  CommonError_Msgs,
  EligibilityCriteria_Msgs,
  hyperlinkTextLineOfTherapy,
} from "../../../Helpers/HelperText";

export const NOTA_VALUE = "NOTA";

let rrmmRowIdSeq = 0;
export const MakeEmptyRrmmRow = () => ({
  rowId: `rrmm-row-${Date.now()}-${rrmmRowIdSeq++}`,
  rrmmTreatmentReceivedId: null, // round-trips the Rrmmtreatmentreceived PK for update-vs-insert matching on save; null for rows not yet persisted
  treatment: null, // one of RRMM_TREATMENT_CONFIG keys, or NOTA_VALUE, or null
  monoCombo: null, // 1 = monotherapy, 2 = combination
  dateRadio: null, // 1 = date entered, 99 = date unknown
  date: "",
  lot: "", // line of therapy (2-40)
});

const RRMMTreatmentCohortTable = ({
  rows,
  treatmentConfig,
  hardErrByRow,
  softErrByRow,
  onRowsChange,
  disabled,
  isLotRangeRestricted = false,
}) => {
  const [list, setList] = useState(rows || []);
  const [showLineOfTherapyModal, setShowLineOfTherapyModal] = useState(false);
  const lockedControlProps = disabled
    ? { style: { pointerEvents: "none" }, tabIndex: -1 }
    : {};

  useEffect(() => {
    setList(rows || []);
  }, [rows]);

  const emitChange = (nextList) => {
    setList(nextList);
    onRowsChange(nextList);
  };

  const usedTreatmentOptions = Object.keys(treatmentConfig).map((k) =>
    Number(k),
  );

  const HandleTreatmentChange = (idx, value) => {
    if (disabled) return;
    const nextList = [...list];
    const parsed = value === NOTA_VALUE ? NOTA_VALUE : Number(value);

    if (parsed === NOTA_VALUE && idx === 0) {
      nextList.splice(0, nextList.length, {
        ...MakeEmptyRrmmRow(),
        rowId: nextList[idx].rowId,
        treatment: NOTA_VALUE,
      });
    } else {
      nextList[idx] = {
        ...nextList[idx],
        treatment: value === "" ? null : parsed,
        monoCombo: null,
        dateRadio: null,
        date: "",
        lot: "",
      };
    }

    emitChange(nextList);
  };

  const HandleMonoComboChange = (idx, value) => {
    if (disabled) return;
    const nextList = [...list];
    nextList[idx] = { ...nextList[idx], monoCombo: Number(value) };
    emitChange(nextList);
  };

  const HandleDateRadioChange = (idx, value) => {
    if (disabled) return;
    const nextList = [...list];
    const valueInt = Number(value);
    nextList[idx] = {
      ...nextList[idx],
      dateRadio: valueInt,
      date: valueInt === 99 ? "" : nextList[idx].date,
    };
    emitChange(nextList);
  };

  const HandleDateChange = (idx, date) => {
    if (disabled) return;
    const nextList = [...list];
    nextList[idx] = { ...nextList[idx], date, dateRadio: 1 };
    emitChange(nextList);
  };

  const HandleLotChange = (idx, e) => {
    if (disabled) return;
    const { value } = e.target;
    const nextList = [...list];
    nextList[idx] = { ...nextList[idx], lot: value };
    emitChange(nextList);
  };

  const HandleAddRow = () => {
    if (disabled) return;
    emitChange([...list, MakeEmptyRrmmRow()]);
  };

  const HandleDeleteRow = (idx) => {
    if (disabled || idx === 0) return;
    emitChange(list.filter((_, i) => i !== idx));
  };

  const anyNota = list?.some((r) => r.treatment === NOTA_VALUE);

  const canAddRows =
    !anyNota &&
    list?.length > 0 &&
    list.some((r) => typeof r.treatment === "number");

  return (
    <div className="dependent-section" style={{ pointerEvents: "auto" }}>
      {list?.map((row, idx) => {
        const cfg =
          typeof row.treatment === "number"
            ? treatmentConfig[row.treatment]
            : null;
        const isRowNota = row.treatment === NOTA_VALUE;
        const rowHardErr = hardErrByRow?.[row.rowId] || {};
        const rowSoftErr = softErrByRow?.[row.rowId] || {};

        return (
          <div className="loop-section-ul" key={row.rowId}>
            <div className="loop-head">
              <span
                className="question-weight"
                style={{ float: "inline-start" }}
              >
                Bispecific/CAR-T Therapy {idx + 1}
              </span>
              {idx !== 0 && (
                <div
                  className="delete-btn-div"
                  style={{ float: "inline-end" }}
                >
                  <img
                    width="16px"
                    src="../Assets/images/Icon-material-delete.png"
                    alt=""
                    style={{
                      cursor: disabled ? "not-allowed" : "pointer",
                      ...lockedControlProps.style,
                    }}
                    onClick={() => HandleDeleteRow(idx)}
                  />
                </div>
              )}
            </div>

            <div className="question-bot-sapce">
              <div className="sub-question grid-overflow">
                <table
                  style={{
                    border: "1px solid black",
                    borderCollapse: "collapse",
                    width: "100%",
                  }}
                >
                  <thead>
                    <tr>
                      <th style={{ ...thStyle, minWidth: "280px" }}>
                        Bispecific/CAR-T Treatment
                      </th>
                      <th style={{ ...thStyle, minWidth: "260px" }}>
                        Monotherapy vs. combination therapy
                      </th>
                      <th style={{ ...thStyle, minWidth: "220px" }}>
                        Date of treatment initiation
                      </th>
                      <th style={{ ...thStyle, minWidth: "110px" }}>
                        <span
                          className="modal-popup"
                          onClick={() => setShowLineOfTherapyModal(true)}
                        >
                          Line of therapy
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      {/* Column A - treatment */}
                      <td style={tdStyle}>
                        <select
                          id={`rrmmTreatment-${idx}`}
                          name={`rrmmTreatment-${idx}`}
                          className="form-select"
                          value={row.treatment ?? ""}
                          onChange={(e) =>
                            HandleTreatmentChange(idx, e.target.value)
                          }
                          {...lockedControlProps}
                        >
                          {/* hidden: still the initial blank value/placeholder
                              until a treatment is chosen, but doesn't appear
                              as a choosable item in the opened dropdown list */}
                          <option value="" hidden>
                            Select
                          </option>
                          {usedTreatmentOptions.map((code) => (
                            <option key={code} value={code}>
                              {treatmentConfig[code].displayLabel}
                            </option>
                          ))}
                          {(idx === 0 || isRowNota) && (
                            <option value={NOTA_VALUE}>
                              None of the above treatments
                            </option>
                          )}
                        </select>
                        <ErrorField
                          show={!!rowHardErr.treatmentMissing}
                          message={EligibilityCriteria_Msgs.S11}
                          style={{
                              display: "block",
                              marginTop: 0,
                              paddingLeft: 0,
                            }}
                        />
                      </td>

                      {/* Column B - monotherapy vs combination */}
                      {isRowNota ? (
                        <td style={tdStyle}>
                          <span className="not-applicable">Not applicable</span>
                        </td>
                      ) : cfg ? (
                        <td style={tdStyle}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                            }}
                          >
                            <input
                              type="radio"
                              id={`rrmmMono-${idx}`}
                              name={`rrmmMonoCombo-${idx}`}
                              value={1}
                              checked={row.monoCombo === 1}
                              onChange={(e) =>
                                HandleMonoComboChange(idx, e.target.value)
                              }
                              {...lockedControlProps}
                              style={{
                                marginTop: "4px",
                                flex: "0 0 auto",
                                ...lockedControlProps.style,
                              }}
                            />
                            <label
                              htmlFor={`rrmmMono-${idx}`}
                              style={{ flex: "1 1 auto" }}
                            >
                              <span className="radio-text-padding">
                                Received as monotherapy
                              </span>
                            </label>
                          </div>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                            }}
                          >
                            <input
                              type="radio"
                              id={`rrmmCombo-${idx}`}
                              name={`rrmmMonoCombo-${idx}`}
                              value={2}
                              checked={row.monoCombo === 2}
                              onChange={(e) =>
                                HandleMonoComboChange(idx, e.target.value)
                              }
                              {...lockedControlProps}
                              style={{
                                marginTop: "4px",
                                flex: "0 0 auto",
                                ...lockedControlProps.style,
                              }}
                            />
                            <label
                              htmlFor={`rrmmCombo-${idx}`}
                              style={{ flex: "1 1 auto" }}
                            >
                              <span className="radio-text-padding">
                                {cfg.comboLabel}
                              </span>
                            </label>
                          </div>
                          <ErrorField
                            show={!!rowHardErr.rbMissing}
                            message={EligibilityCriteria_Msgs.S9}
                          />
                        </td>
                      ) : (
                        <td style={tdStyle}></td>
                      )}

                      {/* Column C - date of treatment initiation */}
                      {isRowNota ? (
                        <td style={tdStyle}>
                          <span className="not-applicable">Not applicable</span>
                        </td>
                      ) : cfg ? (
                        <td style={tdStyle}>
                          <div
                            className="option-without-input"
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            <input
                              type="radio"
                              id={`rrmmDateKnown-${idx}`}
                              name={`rrmmDateRadio-${idx}`}
                              value={1}
                              checked={row.dateRadio === 1}
                              onChange={(e) =>
                                HandleDateRadioChange(idx, e.target.value)
                              }
                              {...lockedControlProps}
                              style={{
                                flex: "0 0 auto",
                                ...lockedControlProps.style,
                              }}
                            />
                            <span
                              className="radio-text-padding"
                              style={{ flex: "1 1 auto", ...lockedControlProps.style }}
                            >
                              <DatePickerComponent
                                ctrlId={`rrmmDate-${idx}`}
                                HandleDateChange={(_, date) =>
                                  HandleDateChange(idx, date)
                                }
                                minDate={dayjs("01-01-1920")}
                                maxDate={dayjs()}
                                locale="en"
                                date={row.date}
                              />
                            </span>
                          </div>
                          <div
                            className="option-without-input"
                            style={{ display: "flex", alignItems: "flex-start" }}
                          >
                            <input
                              type="radio"
                              id={`rrmmDateUnk-${idx}`}
                              name={`rrmmDateRadio-${idx}`}
                              value={99}
                              checked={row.dateRadio === 99}
                              onChange={(e) =>
                                HandleDateRadioChange(idx, e.target.value)
                              }
                              {...lockedControlProps}
                              style={{
                                marginTop: "4px",
                                flex: "0 0 auto",
                                ...lockedControlProps.style,
                              }}
                            />
                            <label
                              htmlFor={`rrmmDateUnk-${idx}`}
                              style={{ flex: "1 1 auto" }}
                            >
                              <span className="radio-text-padding">
                                Date of initiation unknown
                              </span>
                            </label>
                          </div>
                          <ErrorField
                            show={!!rowSoftErr.terminate}
                            message={EligibilityCriteria_Msgs.INELIGIBLE}
                          />
                          <ErrorField
                            show={!!rowSoftErr.dateInvalid}
                            message={
                              EligibilityCriteria_Msgs.RRMMInitiationDateWarning
                            }
                          />
                          <ErrorField
                            show={!!rowSoftErr.ageInvalid}
                            message={
                              EligibilityCriteria_Msgs.PatientUnder18Warning
                            }
                          />
                          <ErrorField
                            show={!!rowHardErr.dateMissing}
                            message={EligibilityCriteria_Msgs.S11Date}
                          />
                          <ErrorField
                            show={!!rowHardErr.dateInvalid}
                            message={CommonError_Msgs.InvalidDate}
                          />
                        </td>
                      ) : (
                        <td style={tdStyle}></td>
                      )}

                      {/* Column D - line of therapy */}
                      {isRowNota ? (
                        <td style={tdStyle}>
                          <span className="not-applicable">Not applicable</span>
                        </td>
                      ) : cfg ? (
                        <td style={tdStyle}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              whiteSpace: "nowrap",
                            }}
                          >
                            <input
                              type="text"
                              className="input-dash-sm rrmm-lot-input"
                              id={`rrmmLot-${idx}`}
                              name={`rrmmLot-${idx}`}
                              autoComplete="off"
                              value={row.lot ?? ""}
                              maxLength={2}
                              onKeyDown={AllowOnlNumeric}
                              onChange={(e) => HandleLotChange(idx, e)}
                              {...lockedControlProps}
                              style={{
                                width: "40px",
                                border: "none",
                                borderBottom: "1px solid #727272",
                                borderRadius: 0,
                                outline: "none",
                                ...lockedControlProps.style,
                              }}
                            />
                            <label
                              htmlFor={`rrmmLot-${idx}`}
                              style={{ marginLeft: "4px" }}
                            >
                              L
                            </label>
                          </div>
                          <ErrorField
                            show={!!rowHardErr.lotRange}
                            message={
                              isLotRangeRestricted
                                ? EligibilityCriteria_Msgs.S11LOTRange2To4
                                : EligibilityCriteria_Msgs.S11LOTRange
                            }
                            style={{
                              display: "block",
                              marginTop: 0,
                              paddingLeft: 0,
                            }}
                          />
                        </td>
                      ) : (
                        <td style={tdStyle}></td>
                      )}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      })}

      {showLineOfTherapyModal && (
        <InfoPopUp
          description={hyperlinkTextLineOfTherapy}
          OkClick={() => setShowLineOfTherapyModal(false)}
        />
      )}

      {canAddRows && (
        <div className="sub-question-bot-sapce-ul mt-2">
          <div
            className="answer-list-text"
            onClick={HandleAddRow}
            style={{
              cursor: disabled ? "not-allowed" : "pointer",
              ...lockedControlProps.style,
            }}
          >
            <span>
              <img width="18px" src="../Assets/images/plus.png" alt="" />
            </span>
            <span
              className="radio-text-padding"
              style={{ color: "blue", textDecoration: "underline" }}
            >
              Click here to add another line of therapy
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

const thStyle = {
  border: "1px solid black",
  padding: "8px",
  textAlign: "center",
};

const tdStyle = {
  border: "1px solid black",
  padding: "8px",
  textAlign: "start",
  verticalAlign: "top",
};

export default RRMMTreatmentCohortTable;
